/**
 * Shared wireframe primitives for landing page A/B variants.
 * Converted from design prototype landing-shared-components.jsx.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";

export function PlayBlob({ size = 60, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 60 60" style={style}>
      <circle cx="30" cy="30" r="26" fill="var(--accent)" stroke="var(--ink)" strokeWidth="2.5" />
      <polygon points="24,18 24,42 44,30" fill="var(--ink)" />
    </svg>
  );
}

export function ThumbBox({ w = 220, h = 130, label, channel, views, duration, accent }: {
  w?: number | string; h?: number; label?: string; channel?: string; views?: string; duration?: string; rot?: number; accent?: string;
}) {
  return (
    <div className="thumb" style={{ width: w, height: h, transform: "none", background: accent || "#e9e2d1" }}>
      {label && <div style={{ position: "absolute", inset: 0, padding: "8px 10px", display: "flex", flexDirection: "column", justifyContent: "space-between", zIndex: 3 }}>
        <div style={{ fontFamily: "var(--display)", fontSize: 14, lineHeight: 1.05, color: "var(--ink)", maxWidth: "75%" }}>{label}</div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
          <div style={{ fontSize: 12, color: "var(--ink)" }}>{channel}<br /><span className="mute" style={{ fontSize: 11 }}>{views}</span></div>
          {duration && <div style={{ background: "var(--ink)", color: "var(--paper)", padding: "1px 5px", fontFamily: "var(--mono)", fontSize: 10 }}>{duration}</div>}
        </div>
      </div>}
    </div>
  );
}

export function FakeWaveform({ width = 280, height = 30, bars = 36, seed = 1 }: { width?: number; height?: number; bars?: number; seed?: number }) {
  const rng = (i: number) => { const x = Math.sin(i * 9.7 + seed * 3.1) * 10000; return Math.abs(x - Math.floor(x)); };
  return (
    <svg width={width} height={height} viewBox={`0 0 ${bars * 6} ${height}`}>
      {Array.from({ length: bars }).map((_, i) => {
        const h = 4 + rng(i) * (height - 6);
        return <rect key={i} x={i * 6 + 1} y={(height - h) / 2} width="3" height={h} fill="var(--ink)" />;
      })}
    </svg>
  );
}

export function ScriptLines({ lines = 6 }: { lines?: number }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%" }}>
      {Array.from({ length: lines }).map((_, i) => {
        const w = 60 + ((i * 23) % 38);
        return <div key={i} style={{ height: 8, width: `${w}%`, borderRadius: 2, background: "rgba(0,0,0,0.18)" }} />;
      })}
    </div>
  );
}

export function ArrowDoodle({ color = "var(--accent)", size = 80, style }: { dir?: string; color?: string; size?: number; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size * 0.5} viewBox="0 0 100 50" style={{ transform: "none", ...style }}>
      <path d="M4 26 Q 30 18 60 26 T 92 24" stroke={color} strokeWidth="3" fill="none" strokeLinecap="round" />
      <path d="M76 12 L 94 24 L 78 38" stroke={color} strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function Squiggle({ color = "var(--ink)", height = 12, opacity = 1 }: { color?: string; height?: number; opacity?: number }) {
  return (
    <svg className="squiggle" viewBox="0 0 800 12" preserveAspectRatio="none" style={{ height, opacity }}>
      <path d="M0 6 Q 20 0 40 6 T 80 6 T 120 6 T 160 6 T 200 6 T 240 6 T 280 6 T 320 6 T 360 6 T 400 6 T 440 6 T 480 6 T 520 6 T 560 6 T 600 6 T 640 6 T 680 6 T 720 6 T 760 6 T 800 6" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" />
    </svg>
  );
}

export function WfNavBar() {
  return (
    <div className="navrow">
      <div className="logo">
        <span className="mark">▶</span>
        <span>TUBEFORGE</span>
      </div>
      <nav>
        <a href="#how-it-works">How it works</a>
        <a href="#faq">FAQ</a>
        <Link to="/login">Login</Link>
      </nav>
      <div className="row" style={{ gap: 10 }}>
        <Link to="/login" className="btn ghost" style={{ padding: "8px 16px", fontSize: 14, textDecoration: "none" }}>Sign in</Link>
        <Link to="/signup" className="btn accent" style={{ padding: "8px 16px", fontSize: 14, textDecoration: "none" }}>Start free →</Link>
      </div>
    </div>
  );
}

export function FaqRow({ q, open: defaultOpen }: { q: string; open?: boolean }) {
  const [isOpen, setIsOpen] = useState(defaultOpen ?? false);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => setIsOpen(o => !o)}
      onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setIsOpen(o => !o); } }}
      style={{ borderBottom: "1px solid var(--rule-2)", padding: "18px 0", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 24, cursor: "pointer" }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ fontFamily: "var(--display)", fontSize: 20 }}>{q}</div>
        {isOpen && <div className="type mute" style={{ marginTop: 8, fontSize: 16, lineHeight: 1.5 }}><ScriptLines lines={2} /></div>}
      </div>
      <div style={{ fontFamily: "var(--display)", fontSize: 26, lineHeight: 0.7, transition: "transform .2s ease", transform: isOpen ? "none" : "rotate(0deg)" }}>{isOpen ? "–" : "+"}</div>
    </div>
  );
}

export function FooterCTA({ headline = "Ready to ship?", sub = "2 free credits. No card. Cancel anytime." }: { headline?: string; sub?: string }) {
  return (
    <div style={{ padding: "70px 60px", borderTop: "1px solid var(--rule-2)", background: "var(--ink)", color: "var(--paper)", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 14, position: "relative" }}>
      <div style={{ fontFamily: "var(--display)", fontSize: 64, lineHeight: 0.95 }}>{headline}</div>
      <div className="type" style={{ fontSize: 20, opacity: 0.75 }}>{sub}</div>
      <div style={{ marginTop: 18, display: "flex", gap: 14 }}>
        <Link to="/signup" className="btn accent" style={{ fontSize: 22, padding: "14px 28px", textDecoration: "none" }}>▶ Start free trial</Link>
        <Link to="/home-a" className="btn" style={{ background: "transparent", color: "var(--paper)", borderColor: "var(--paper)", fontSize: 22, padding: "14px 28px", textDecoration: "none" }}>See a sample</Link>
      </div>
    </div>
  );
}

export function WfFooter() {
  return (
    <div style={{ padding: "30px 60px", borderTop: "1px solid var(--rule-2)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
      <div className="logo" style={{ fontSize: 18 }}><span className="mark" style={{ width: 24, height: 24, fontSize: 13 }}>▶</span><span>TUBEFORGE</span></div>
      <div className="row" style={{ gap: 18 }}>
        {[
          { label: "how it works", href: "#how-it-works" },
          { label: "faq", href: "#faq" },
          { label: "login", to: "/login" },
          { label: "Privacy Policy", to: "/privacy" },   // (B120-1; D5) the footer's names
          { label: "Terms of Service", to: "/terms" },
          { label: "Data deletion", to: "/data-deletion" },   // (B120) in the home page's raw HTML
          { label: "Contact", to: "/contact" },
        ].map(x => (
          "to" in x
            ? <Link key={x.label} to={x.to!} className="type mute" style={{ textDecoration: "none" }}>{x.label}</Link>
            : <a key={x.label} href={x.href} className="type mute" style={{ textDecoration: "none" }}>{x.label}</a>
        ))}
      </div>
      <div className="type mute" style={{ fontSize: 13 }}>© 2026 — handmade in the cloud</div>
    </div>
  );
}
